import {useMemo} from 'react';
import {CellValue, DurationUnit, ReportRow} from '../data/reportRowModel';

export type GroupingToken = 'project' | 'phase' | 'client' | 'role' | 'spint' | 'task' | 'assignee';

export interface GroupHeaderRow extends ReportRow {
	isGroupHeader: true;
	groupLabel: string;
	groupCount: number;
	/** duration totals per unit; null when units mixed (suppressed) */
	groupTotals: Record<string, {minutes: number; unit: DurationUnit} | null>;
}

/** Token -> cell path used to derive each row's group label for that token. Every grouping
 * token must have an entry here even if current fixtures lack the cell — the label falls back
 * to 'No <token>' when the cell is missing on a given row. */
const GROUPING_CELL_PATH: Record<GroupingToken, string> = {
	project: 'task.project.info',
	phase: 'task.phase.name',
	client: 'task.client.name',
	role: 'task.role.info',
	spint: 'task.sprint.name',
	task: 'task.name',
	assignee: 'task.assignees.firstName',
};

/** Fallback 'No <label>' text per token when the group-defining cell is absent. 'spint' (legacy
 * spelling for 'sprint') gets the corrected 'No sprint' label per the brief. */
const NO_GROUP_LABEL: Record<GroupingToken, string> = {
	project: 'No project',
	phase: 'No phase',
	client: 'No client',
	role: 'No role',
	spint: 'No sprint',
	task: 'No task',
	assignee: 'No assignee',
};

/** Extracts the display label(s) a row contributes to grouping by `token`. Most cell kinds
 * contribute a single label; the 'persons' cell (used for assignee) contributes one label per
 * person so a multi-assignee row is duplicated across each assignee's group. An empty array
 * means the row falls back to the token's 'No <x>' bucket. */
function labelsForToken(row: ReportRow, token: GroupingToken): string[] {
	const cell: CellValue | undefined = row.cells[GROUPING_CELL_PATH[token]];
	if (cell === undefined) {
		return [];
	}
	switch (cell.kind) {
		case 'persons':
			return cell.persons.map(p => p.name);
		case 'text':
			return cell.value ? [cell.value] : [];
		case 'project':
			return [cell.name];
		case 'status':
			return [cell.label];
		default:
			return [];
	}
}

interface Bucket {
	label: string;
	rows: ReportRow[];
}

/** Buckets top-level `rows` by the labels they contribute for `token`, preserving first-seen
 * label order. A row contributing multiple labels (multi-assignee) is pushed into each of its
 * buckets; a row contributing none goes into the token's 'No <x>' bucket. */
function bucketRows(rows: ReportRow[], token: GroupingToken): Bucket[] {
	const buckets = new Map<string, Bucket>();

	for (const row of rows) {
		const labels = labelsForToken(row, token);
		const targetLabels = labels.length > 0 ? labels : [NO_GROUP_LABEL[token]];
		for (const label of targetLabels) {
			let bucket = buckets.get(label);
			if (!bucket) {
				bucket = {label, rows: []};
				buckets.set(label, bucket);
			}
			bucket.rows.push(row);
		}
	}

	return Array.from(buckets.values());
}

/** Sums duration cells at `path` across `rows` (each row's own cell only, not recursing into
 * children). Returns null when zero contributing rows have a duration cell at that path, or when
 * more than one distinct unit is present among contributing rows (cross-unit sums are
 * suppressed, never silently combined). */
function sumDurationsAtPath(rows: ReportRow[], path: string): {minutes: number; unit: DurationUnit} | null {
	let minutes = 0;
	let unit: DurationUnit | null = null;
	let sawMixedUnits = false;
	let contributed = false;

	for (const row of rows) {
		const cell = row.cells[path];
		if (cell === undefined || cell.kind !== 'duration') {
			continue;
		}
		contributed = true;
		if (unit === null) {
			unit = cell.unit;
		} else if (unit !== cell.unit) {
			sawMixedUnits = true;
		}
		minutes += cell.minutes;
	}

	if (!contributed || sawMixedUnits || unit === null) {
		return null;
	}
	return {minutes, unit};
}

/** Returns the row's stable identity for de-dup purposes: the id up to (excluding) the first
 * `::`, which strips the `::<label>` suffix `withDecollidedId` adds to multi-assignee duplicates.
 * A task duplicated across several assignee sub-groups shares this base id across all its copies. */
function baseRowId(row: ReportRow): string {
	const suffixIndex = row.id.indexOf('::');
	return suffixIndex === -1 ? row.id : row.id.slice(0, suffixIndex);
}

/** Recursively collects the descendant leaf DATA rows under `rows` for group-totalling purposes:
 * recurses through `GroupHeaderRow.children` (synthetic nesting levels) but treats any real
 * `ReportRow` as a leaf and does not descend into its own subtask/deliverable `children` — matches
 * the pre-existing (unreviewed-as-wrong) leaf-level behaviour of only totalling each data row's own
 * cells, not its hierarchy's. De-duplicates by base id (see `baseRowId`) so a multi-assignee task
 * duplicated across several assignee sub-groups is counted once toward an ancestor total. */
function collectDescendantDataRows(rows: ReportRow[]): ReportRow[] {
	const seen = new Set<string>();
	const result: ReportRow[] = [];

	function visit(row: ReportRow): void {
		if ((row as GroupHeaderRow).isGroupHeader === true) {
			for (const child of row.children) {
				visit(child);
			}
			return;
		}
		const id = baseRowId(row);
		if (seen.has(id)) {
			return;
		}
		seen.add(id);
		result.push(row);
	}

	for (const row of rows) {
		visit(row);
	}
	return result;
}

/** Computes per-column duration totals for a group, from its descendant leaf data rows (see
 * `collectDescendantDataRows`) — not the group's immediate `children`, which for any non-leaf
 * nesting level are themselves synthetic `GroupHeaderRow`s with empty `cells` and would otherwise
 * make every ancestor level's totals silently empty. Totals across every field path that appears as
 * a duration cell on at least one contributing leaf row. */
function computeGroupTotals(children: ReportRow[]): Record<string, {minutes: number; unit: DurationUnit} | null> {
	const rows = collectDescendantDataRows(children);

	const paths = new Set<string>();
	for (const row of rows) {
		for (const [path, cell] of Object.entries(row.cells)) {
			if (cell.kind === 'duration') {
				paths.add(path);
			}
		}
	}

	const totals: Record<string, {minutes: number; unit: DurationUnit} | null> = {};
	for (const path of paths) {
		totals[path] = sumDurationsAtPath(rows, path);
	}
	return totals;
}

/** Builds a group header pseudo-row. Its id embeds the full token/label path so nested grouping
 * levels (and repeated labels across sibling branches, e.g. the same assignee under two
 * different projects) never collide. It satisfies `ReportRow` so Task 3's table renders its
 * `children` through the same subrow mechanism as real rows. */
function buildGroupHeaderRow(idPath: string, token: GroupingToken, label: string, children: ReportRow[]): GroupHeaderRow {
	return {
		id: `group:${token}:${idPath}`,
		type: 'task',
		parentId: null,
		children,
		matchedFilter: true,
		favorite: false,
		cells: {},
		isGroupHeader: true,
		groupLabel: label,
		// Descendant leaf DATA rows, not immediate `children`: at any non-leaf grouping level the
		// immediate children are themselves synthetic group headers, so `children.length` would
		// report the sub-group count instead of the number of tasks under this group.
		groupCount: collectDescendantDataRows(children).length,
		groupTotals: computeGroupTotals(children),
	};
}

/** De-collides a row's id for the group branch it has been duplicated into. Only rows that
 * contribute more than one label for the current token (multi-assignee tasks) are actually
 * duplicated across branches; giving every duplicate a `::<groupLabel>` suffix keeps React keys
 * and TanStack's internal row ids unique even though the underlying row object is shared across
 * groups. Children keep their original ids since they are not independently duplicated. */
function withDecollidedId(row: ReportRow, groupLabel: string, duplicated: boolean): ReportRow {
	if (!duplicated) {
		return row;
	}
	return {...row, id: `${row.id}::${groupLabel}`};
}

/** Recursively groups `rows` by the ordered `grouping` tokens (max 4 levels). Only top-level
 * rows are distributed into groups at each level; each row's own `children` (subtasks/deliverables)
 * stay attached beneath it, untouched, inside whichever group it lands in. */
function groupRowsRecursive(rows: ReportRow[], grouping: GroupingToken[], idPathPrefix: string): ReportRow[] {
	if (grouping.length === 0) {
		return rows;
	}

	const [token, ...restTokens] = grouping;
	const buckets = bucketRows(rows, token);

	return buckets.map(bucket => {
		const branchRows = bucket.rows.map(row => {
			const rowDuplicated = labelsForToken(row, token).length > 1;
			return withDecollidedId(row, bucket.label, rowDuplicated);
		});
		const idPath = idPathPrefix ? `${idPathPrefix}/${bucket.label}` : bucket.label;
		const nestedChildren = groupRowsRecursive(branchRows, restTokens, idPath);
		return buildGroupHeaderRow(idPath, token, bucket.label, nestedChildren);
	});
}

/** Pure grouping transform: groups only top-level `rows` (never descends into `children`) by the
 * ordered `grouping` tokens, nesting group header pseudo-rows up to 4 levels deep. Duplicates
 * multi-assignee rows across each contributing assignee's group. Returns `rows` unchanged (same
 * reference) when `grouping` is empty. */
export function groupRows(rows: ReportRow[], grouping: GroupingToken[]): ReportRow[] {
	if (grouping.length === 0) {
		return rows;
	}
	// definition contract caps grouping at 4 levels regardless of how many tokens the caller passes.
	const cappedGrouping = grouping.slice(0, 4);
	return groupRowsRecursive(rows, cappedGrouping, '');
}

/** Thin memoization wrapper around `groupRows` for use in table components. */
export function useClientGrouping(rows: ReportRow[], grouping: GroupingToken[]): ReportRow[] {
	return useMemo(() => groupRows(rows, grouping), [rows, grouping]);
}
